import memoize from 'lru-memoize';
import React, {FC, useMemo, useState} from 'react';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {
  SearchDropdown,
  SearchDropdownProps,
} from '../../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {doesTeammateMatchQuery} from '../../../../../../helpers/modelHelpers';
import {DropdownItemTeammateAvatar} from '../../../../../common/dropdowns/dropdownItemTeammateAvatar';

/*
 * Props.
 */

export interface MultiSelectSearchTeammateDropdownProps
  extends DropdownProps,
    Pick<SearchDropdownProps<TeammateModel>, 'headerTitle'> {
  teammates: ReadonlyArray<TeammateModel>;
  initiallySelectedAliases: ReadonlySet<string>;
  onAliasesSelectionChange: (arg: ReadonlySet<string>) => void;
}

/*
 * Components.
 */

export const MultiSelectSearchTeammateDropdown: FC<MultiSelectSearchTeammateDropdownProps> = (props) => {
  const {
    teammates,
    initiallySelectedAliases,
    onAliasesSelectionChange,
    headerTitle,
    onRequestClose,
    ...dropdownProps
  } = props;

  const [query, setQuery] = useState('');
  const filteredData = useMemo(() => filterRows(teammates, query), [teammates, query]);
  const [selection, setSelection] = useState(new Set(initiallySelectedAliases));

  const onSelection = (teammate: TeammateModel) => {
    const newSelection = new Set(selection);

    if (newSelection.has(teammate.alias)) {
      newSelection.delete(teammate.alias);
    } else {
      newSelection.add(teammate.alias);
    }

    setSelection(newSelection);
  };

  const reportChangeAndClose = () => {
    onAliasesSelectionChange(selection);
    onRequestClose?.();
  };

  return (
    <SearchDropdown
      {...dropdownProps}
      onRequestClose={reportChangeAndClose}
      data={filteredData}
      query={query}
      onQueryChange={setQuery}
      renderRow={(row) => renderTeammate(row, selection, onSelection)}
      headerTitle={headerTitle ?? <Trans id="customerview-search-teammate-title">Teammate</Trans>}
      headerRightIcon={
        selection.size > 0 ? (
          <Button
            variant={ButtonVariantsEnum.LINK}
            size={VisualSizesEnum.SMALL}
            onClick={() => {
              selection.clear();
              setSelection(selection);
              reportChangeAndClose();
            }}
          >
            <Trans id="clear-refinement-button-link">Clear</Trans>
          </Button>
        ) : null
      }
    />
  );
};

/*
 * Helpers.
 */

function renderTeammate(
  teammate: TeammateModel,
  selectedAliases: ReadonlySet<string>,
  onSelect: (model: TeammateModel) => void,
) {
  return (
    <DropdownItem
      onSelect={(event) => {
        // Prevent the dropdown from closing.
        event.preventDefault();
        onSelect(teammate);
      }}
      renderRightContent={() => <DropdownItemCheckbox isChecked={selectedAliases.has(teammate.alias)} />}
    >
      <DropdownItemTeammateAvatar teammate={teammate} />
      <DropdownItemContent>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
    </DropdownItem>
  );
}

const filterRows = memoize(20)((rows: ReadonlyArray<TeammateModel>, query: string) => {
  if (!query) {
    return rows;
  }

  return rows.filter((t) => doesTeammateMatchQuery(t, query));
});
